import React, { useEffect, useRef, useState } from 'react';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';

/**
 * Async/sync typeahead input with ArrowUp/ArrowDown/Enter support.
 *
 * Either pass `options` (controlled list) + `loading`, or `fetchOptions(keyword) => Promise<item[]>`.
 */
const TypeaheadInput = ({
  value,
  onChange,
  onSelect,
  options: controlledOptions,
  fetchOptions,
  loading: controlledLoading,
  minChars = 2,
  debounceMs = 300,
  placeholder = 'Search...',
  disabled = false,
  required = false,
  label,
  getOptionKey = (item) => item?.Id,
  getDisplayValue = (item) => item?.Name || '',
  renderOption,
  inputClassName = 'w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent',
  emptyText = 'No results found',
  disabledPlaceholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const selectedItem = typeof value === 'object' && value ? value : null;
  const displayText = isEditing || !selectedItem ? searchTerm : getDisplayValue(selectedItem);

  const options = controlledOptions ?? asyncOptions;
  const loading = controlledLoading ?? asyncLoading;
  const showList = isOpen && searchTerm.trim().length >= minChars;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!fetchOptions) return undefined;

    const term = searchTerm.trim();
    if (term.length < minChars) {
      setAsyncOptions([]);
      setAsyncLoading(false);
      return undefined;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setAsyncLoading(true);
        const result = await fetchOptions(term);
        const list = Array.isArray(result)
          ? result
          : result?.Data ?? result?.data ?? [];
        setAsyncOptions(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Typeahead fetch error:', err);
        setAsyncOptions([]);
      } finally {
        setAsyncLoading(false);
      }
    }, debounceMs);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, fetchOptions, minChars, debounceMs]);

  const handleSelect = (item) => {
    if (onSelect) onSelect(item);
    else if (onChange) onChange(item);
    setIsOpen(false);
    setIsEditing(false);
    setSearchTerm('');
  };

  const { highlightedIndex, setHighlightedIndex, onKeyDown, setItemRef } = useListKeyboardNav({
    isOpen: showList && !loading && options.length > 0,
    itemCount: options.length,
    onSelectIndex: (index) => {
      const item = options[index];
      if (item) handleSelect(item);
    },
    onClose: () => setIsOpen(false),
    resetKey: `${searchTerm}:${options.length}`,
  });

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={displayText}
          onChange={(e) => {
            const next = e.target.value;
            setIsEditing(true);
            setSearchTerm(next);
            setIsOpen(true);
            if (selectedItem && onChange) onChange(null);
            if (onChange && !onSelect && typeof value === 'string') onChange(next);
          }}
          onFocus={() => {
            setIsEditing(true);
            if (!searchTerm && selectedItem) setSearchTerm(getDisplayValue(selectedItem));
            if (searchTerm.trim().length >= minChars || getDisplayValue(selectedItem || {}).length >= minChars) {
              setIsOpen(true);
            }
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={disabled && disabledPlaceholder ? disabledPlaceholder : placeholder}
          className={`${inputClassName} ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : ''}`}
        />

        {showList && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="max-h-60 overflow-y-auto" role="listbox">
              {loading ? (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">Loading...</div>
              ) : options.length > 0 ? (
                options.map((item, index) => {
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <button
                      key={getOptionKey(item) ?? index}
                      type="button"
                      role="option"
                      aria-selected={isHighlighted}
                      ref={(node) => setItemRef(index, node)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(item)}
                      className={`w-full px-4 py-2 text-left transition-colors ${
                        isHighlighted
                          ? 'bg-[#f0f9f4] text-[#46955A] font-medium'
                          : 'text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {renderOption ? renderOption(item) : getDisplayValue(item)}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">{emptyText}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeaheadInput;
